/**
 * Calendar Integrations Dialog
 * 
 * Dialog for connecting calendar providers and managing existing integrations.
 * Uses CalendarList component for displaying connected accounts.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { useAuth } from '@/context/auth-context'
import { useCalendars } from '@/context/calendars-context'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { openOAuthPopup } from '@/lib/oauth-popup'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Loader } from '@/components/ai-elements/loader'
import { IntegrationDialogLayout } from './integration-dialog-layout'
import { CalendarList } from '@/components/automation-rules/calendar-list'
import { CalendarsIcon } from '@/components/icons'
import { SectionHeader } from '@/components/ui/section-header'
import type { Provider, IntegrationConfig } from './types'

const CALENDAR_INTEGRATIONS_CONFIG: IntegrationConfig = {
  type: 'calendar',
  icon: CalendarsIcon,
  title: 'Calendar integrations',
  description: 'Connect your calendar provider accounts to create automation rules and enable analytics.',
  integrateLabel: 'Integrate your calendars',
  loadingLabel: 'Loading calendars...',
}

interface CalendarIntegrationsDialogProps {
  open: boolean
  onClose: () => void
  /** Optional callback when a calendar is successfully connected (for onboarding) */
  onSuccess?: () => void
}

/**
 * Helper function to normalize logo path
 */
const normalizeLogoPath = (logoPath?: string) => {
  if (!logoPath) return ''
  return logoPath.replace(/\\/g, '/').replace(/^public\//, '/')
}

export function CalendarIntegrationsDialog({ open, onClose, onSuccess }: CalendarIntegrationsDialogProps) {
  const { session, user } = useAuth()
  const { integrations, loading: calendarsLoading, refreshCalendars } = useCalendars()
  
  // State for providers (fetched directly from Supabase)
  const [providers, setProviders] = useState<Provider[]>([])
  const [providersLoading, setProvidersLoading] = useState(true)
  const initialFetchDone = useRef(false)
  
  // Track active OAuth channels for proper cleanup
  const activeChannelsRef = useRef<Set<ReturnType<typeof supabase.channel>>>(new Set())

  /**
   * Fetch providers from Supabase
   */
  const fetchProviders = useCallback(async () => {
    if (!session) {
      setProvidersLoading(false)
      return
    }

    try {
      setProvidersLoading(true)

      const { data: providersData, error: providersError } = await supabase
        .from('integrations_providers')
        .select('id, display_name, logo, url, client_id_app_env_var, disabled')
        .eq('type', 'calendar')

      if (providersError) {
        console.error('Providers fetch error:', providersError)
        toast.error('Failed to load providers')
        return
      }

      const mappedProviders: Provider[] = (providersData || []).map((p: any) => ({
        id: p.id,
        display_name: p.display_name,
        logo: p.logo || '',
        url: p.url || '',
        client_id_app_env_var: p.client_id_app_env_var || '',
        disabled: p.disabled ?? false,
      }))

      setProviders(mappedProviders)
    } catch (error) {
      console.error('Providers fetch error:', error)
      toast.error('Failed to load providers')
    } finally {
      setProvidersLoading(false)
    }
  }, [session])

  /**
   * Fetch providers on mount and when dialog opens
   */
  useEffect(() => {
    if (open && session && !initialFetchDone.current) {
      initialFetchDone.current = true
      fetchProviders()
    }
  }, [open, session, fetchProviders])

  /**
   * Reset state when dialog closes
   */
  useEffect(() => {
    if (!open) {
      setProviders([])
      setProvidersLoading(true)
      initialFetchDone.current = false
      
      // Cleanup all active OAuth channels
      activeChannelsRef.current.forEach(channel => {
        channel.unsubscribe()
      })
      activeChannelsRef.current.clear()
    }
  }, [open])
  
  /**
   * Cleanup channels on unmount
   */
  useEffect(() => {
    return () => {
      activeChannelsRef.current.forEach(channel => {
        channel.unsubscribe()
      })
      activeChannelsRef.current.clear()
    }
  }, [])

  /**
   * Handle connecting a provider via OAuth
   */
  const handleConnectProvider = useCallback(async (provider: Provider) => {
    if (!session?.access_token || !user?.id) {
      toast.error('Authentication required')
      return
    }

    try {
      // Create a unique channel for this OAuth flow
      const channelId = `oauth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const channel = supabase.channel(channelId)

      const cleanupChannel = () => {
        channel.unsubscribe()
        activeChannelsRef.current.delete(channel)
      }

      activeChannelsRef.current.add(channel)

      // Listen for OAuth callback
      let broadcastReceived = false
      channel
        .on('broadcast', { event: 'integration_complete' }, async (payload) => {
          broadcastReceived = true
          if (payload.payload.success) {
            // Add delay to ensure database transaction is committed
            await new Promise(r => setTimeout(r, 1000))
            await refreshCalendars()
            toast.success(`${provider.display_name} connected successfully!`)
            onSuccess?.()
          } else {
            if (payload.payload.error !== 'OAuth popup was closed') {
              toast.error(payload.payload.error || 'Connection failed')
            }
          }
          cleanupChannel()
        })
        .on('broadcast', { event: 'oauth_callback' }, async (payload) => {
          // Handle legacy event name for backward compatibility
          broadcastReceived = true
          if (payload.payload.success) {
            // Add delay to ensure database transaction is committed
            await new Promise(r => setTimeout(r, 1000))
            await refreshCalendars()
            toast.success(`${provider.display_name} connected successfully!`)
            onSuccess?.()
          } else {
            if (payload.payload.error !== 'OAuth popup was closed') {
              toast.error(payload.payload.error || 'Connection failed')
            }
          }
          cleanupChannel()
        })
        .subscribe()

      // Store OAuth state
      const oauthState = {
        integrationType: 'calendar' as const,
        returnView: 'list' as const
      }
      localStorage.setItem('integration_oauth_state', JSON.stringify(oauthState))

      // Construct OAuth URL
      const appOrigin = btoa(window.location.origin)
      const state = `${session.access_token}|||${appOrigin}|||${provider.id}|||${channelId}`
      const providerClientId = import.meta.env[provider.client_id_app_env_var]

      if (!providerClientId) {
        toast.error(`${provider.display_name} Client ID not configured`)
        cleanupChannel()
        return
      }

      const providerOAuthUrl = `${provider.url}&client_id=${providerClientId}&state=${encodeURIComponent(state)}`

      // Open OAuth in popup
      const result = await openOAuthPopup({
        url: providerOAuthUrl,
        title: `Connect ${provider.display_name}`
      })

      // Handle result from postMessage (fallback if broadcast doesn't work)
      if (result.success && !broadcastReceived) {
        // Add delay to ensure database transaction is committed
        await new Promise(r => setTimeout(r, 1000))
        await refreshCalendars()
        toast.success(`${provider.display_name} connected successfully!`)
        onSuccess?.()
        cleanupChannel()
      } else if (result.error && !broadcastReceived) {
        if (result.error !== 'OAuth popup was closed') {
          toast.error(result.error)
        }
        cleanupChannel()
      }
      
      // Cleanup after timeout
      setTimeout(() => {
        if (activeChannelsRef.current.has(channel)) {
          cleanupChannel()
        }
      }, 30000)
      
    } catch (error) {
      toast.error(`Failed to initiate ${provider.display_name} connection`)
      console.error('Provider OAuth error:', error)
    }
  }, [session?.access_token, user?.id, refreshCalendars, onSuccess])

  // Show loading state while providers are being fetched
  if (providersLoading) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent 
          className="
            max-w-none
            p-0
            border-0 shadow-none
            w-full h-[90vh] max-h-[90vh]
            md:w-[800px] md:h-[70vh] md:max-h-[70vh]
            flex flex-col
            fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
            md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
            rounded-t-3xl md:rounded-lg
          "
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          hideClose
        >
          <DialogTitle className="sr-only">{CALENDAR_INTEGRATIONS_CONFIG.title}</DialogTitle>
          <DialogDescription className="sr-only">{CALENDAR_INTEGRATIONS_CONFIG.description}</DialogDescription>
          <IntegrationDialogLayout title={CALENDAR_INTEGRATIONS_CONFIG.title} onBack={onClose}>
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <Loader size={24} className="text-muted-foreground" />
              <div className="text-muted-foreground">{CALENDAR_INTEGRATIONS_CONFIG.loadingLabel}</div>
            </div>
          </IntegrationDialogLayout>
        </DialogContent>
      </Dialog>
    )
  }

  const hasProviders = providers.length > 0
  const hasIntegrations = integrations.length > 0

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[90vh] max-h-[90vh]
          md:w-[800px] md:h-[70vh] md:max-h-[70vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogTitle className="sr-only">{CALENDAR_INTEGRATIONS_CONFIG.title}</DialogTitle>
        <DialogDescription className="sr-only">{CALENDAR_INTEGRATIONS_CONFIG.description}</DialogDescription>
        <IntegrationDialogLayout title={CALENDAR_INTEGRATIONS_CONFIG.title} onBack={onClose}>
          <div className="space-y-6">
            {/* Connect Providers Section */}
            {hasProviders && (
              <div>
                <SectionHeader title={CALENDAR_INTEGRATIONS_CONFIG.integrateLabel} />
                <Card className="overflow-hidden">
                  <CardContent className="p-0 flex flex-col items-center justify-center space-y-3 py-4">
                    {providers.map((provider) => (
                      <Button 
                        key={provider.id}
                        onClick={() => handleConnectProvider(provider)}
                        variant="outline"
                        className="flex items-center gap-2 w-full max-w-xs"
                        disabled={provider.disabled}
                      >
                        <img 
                          src={normalizeLogoPath(provider.logo)} 
                          alt={provider.display_name}
                          className="w-5 h-5"
                        />
                        {provider.display_name}
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Manage Integrations Section */}
            {(hasIntegrations || calendarsLoading) && (
              <div>
                <SectionHeader title="Manage integrations" />
                <CalendarList
                  isSelectable={false}
                  isLocked={false}
                  showDisconnect={true}
                />
              </div>
            )}

            {/* Empty State */}
            {!hasProviders && !hasIntegrations && !calendarsLoading && (
              <div className="text-center py-8 text-muted-foreground">
                No integrations available
              </div>
            )}
          </div>
        </IntegrationDialogLayout>
      </DialogContent>
    </Dialog>
  )
}
